import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Crown, Briefcase, Rocket, Star,
  Search, Filter, MoreVertical, Check, X,
  Edit, Eye, Mail, Calendar, CreditCard,
  TrendingUp, AlertCircle, RefreshCw, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Couleurs de la charte
const C = {
  navy: '#1e3a5f',
  orange: '#f97316',
  green: '#22c55e',
  white: '#ffffff',
};

// Configuration des plans
const PLANS_CONFIG = {
  gratuit: {
    name: 'Gratuit',
    color: C.green,
    bgColor: '#DCFCE7',
    icon: Star,
    price: { monthly: 0, annual: 0 },
    maxUsers: 1,
  },
  pro: {
    name: 'Pro',
    color: '#3B82F6',
    bgColor: '#DBEAFE',
    icon: Briefcase,
    price: { monthly: 75, annual: 50 },
    maxUsers: 5,
  },
  business: {
    name: 'Business',
    color: C.orange,
    bgColor: '#FED7AA',
    icon: Rocket,
    price: { monthly: 100, annual: 75 },
    maxUsers: 20,
  },
  premium: {
    name: 'Premium',
    color: C.navy,
    bgColor: '#E0E7FF',
    icon: Crown,
    price: { monthly: 125, annual: 100 },
    maxUsers: 50,
  },
};

// Composant carte statistique
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    style={{
      background: '#fff',
      borderRadius: 16,
      padding: 24,
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={24} color={color} />
      </div>
      {trend && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          color: trend > 0 ? C.green : '#ef4444',
          fontSize: 13, fontWeight: 600,
        }}>
          <TrendingUp size={16} style={{ transform: trend < 0 ? 'rotate(180deg)' : 'none' }} />
          {Math.abs(trend)}%
        </div>
      )}
    </div>
    <div style={{ fontSize: 32, fontWeight: 800, color: C.navy, marginBottom: 4 }}>
      {value}
    </div>
    <div style={{ fontSize: 14, color: '#64748b' }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{subtitle}</div>}
  </motion.div>
);

// Composant badge de plan
const PlanBadge = ({ plan }) => {
  const config = PLANS_CONFIG[plan] || PLANS_CONFIG.gratuit;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 999,
      background: config.bgColor,
      color: config.color,
      fontSize: 12, fontWeight: 600,
    }}>
      <config.icon size={14} />
      {config.name}
    </span>
  );
};

// Composant badge de statut
const StatusBadge = ({ status }) => {
  const configs = {
    active: { bg: '#DCFCE7', color: '#166534', label: 'Actif' },
    pending: { bg: '#FEF3C7', color: '#92400E', label: 'En attente' },
    expired: { bg: '#FEE2E2', color: '#991B1B', label: 'Expiré' },
    cancelled: { bg: '#F3F4F6', color: '#6B7280', label: 'Annulé' },
  };
  const config = configs[status] || configs.pending;
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 999,
      background: config.bg, color: config.color,
      fontSize: 11, fontWeight: 600,
    }}>
      {config.label}
    </span>
  );
};

// Modal de détail/édition
const SubscriptionModal = ({ subscription, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    plan: subscription?.plan || 'gratuit',
    status: subscription?.status || 'active',
    billing_cycle: subscription?.billing_cycle || 'monthly',
    notes: subscription?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(subscription._id || subscription.id, formData);
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
    }
    setSaving(false);
  };

  if (!subscription) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          background: '#fff', borderRadius: 20,
          width: '100%', maxWidth: 600,
          maxHeight: '90vh', overflow: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.navy, margin: 0 }}>
              Gérer l'abonnement
            </h2>
            <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>
              {subscription.company_name || subscription.user_name}
            </p>
          </div>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: 10,
            border: 'none', background: '#f1f5f9',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={18} color="#64748b" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '28px' }}>
          {/* Infos client */}
          <div style={{
            background: '#f8fafc', borderRadius: 12, padding: 20, marginBottom: 24,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Email</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.navy }}>{subscription.email}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Date d'inscription</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.navy }}>
                  {new Date(subscription.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Dernière facturation</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.navy }}>
                  {subscription.last_billing_date ? new Date(subscription.last_billing_date).toLocaleDateString('fr-FR') : '-'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Prochaine facturation</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.navy }}>
                  {subscription.next_billing_date ? new Date(subscription.next_billing_date).toLocaleDateString('fr-FR') : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Plan */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 8 }}>
                Formule d'abonnement
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {Object.entries(PLANS_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => setFormData({ ...formData, plan: key })}
                    style={{
                      padding: '14px 12px', borderRadius: 12,
                      border: `2px solid ${formData.plan === key ? config.color : '#e2e8f0'}`,
                      background: formData.plan === key ? `${config.color}10` : '#fff',
                      cursor: 'pointer', textAlign: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <config.icon size={20} color={config.color} style={{ margin: '0 auto 8px' }} />
                    <div style={{ fontSize: 13, fontWeight: 600, color: config.color }}>{config.name}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                      {config.price.monthly}€/mois
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Statut */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 8 }}>
                Statut de l'abonnement
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { value: 'active', label: 'Actif', color: C.green },
                  { value: 'pending', label: 'En attente', color: '#F59E0B' },
                  { value: 'expired', label: 'Expiré', color: '#EF4444' },
                  { value: 'cancelled', label: 'Annulé', color: '#6B7280' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setFormData({ ...formData, status: opt.value })}
                    style={{
                      flex: 1, padding: '12px', borderRadius: 10,
                      border: `2px solid ${formData.status === opt.value ? opt.color : '#e2e8f0'}`,
                      background: formData.status === opt.value ? `${opt.color}10` : '#fff',
                      cursor: 'pointer',
                      fontSize: 13, fontWeight: 500, color: opt.color,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Cycle de facturation */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 8 }}>
                Cycle de facturation
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setFormData({ ...formData, billing_cycle: 'monthly' })}
                  style={{
                    flex: 1, padding: '14px', borderRadius: 10,
                    border: `2px solid ${formData.billing_cycle === 'monthly' ? C.navy : '#e2e8f0'}`,
                    background: formData.billing_cycle === 'monthly' ? `${C.navy}10` : '#fff',
                    cursor: 'pointer',
                    fontSize: 14, fontWeight: 500, color: C.navy,
                  }}
                >
                  Mensuel
                </button>
                <button
                  onClick={() => setFormData({ ...formData, billing_cycle: 'annual' })}
                  style={{
                    flex: 1, padding: '14px', borderRadius: 10,
                    border: `2px solid ${formData.billing_cycle === 'annual' ? C.orange : '#e2e8f0'}`,
                    background: formData.billing_cycle === 'annual' ? `${C.orange}10` : '#fff',
                    cursor: 'pointer',
                    fontSize: 14, fontWeight: 500, color: C.orange,
                  }}
                >
                  Annuel (-2 mois)
                </button>
              </div>
            </div>

            {/* Notes admin */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 8 }}>
                Notes internes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notes visibles uniquement par l'admin..."
                style={{
                  width: '100%', padding: 14, borderRadius: 10,
                  border: '1px solid #e2e8f0', fontSize: 14,
                  minHeight: 80, resize: 'vertical',
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex', gap: 12, justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px', borderRadius: 10,
              border: '1px solid #e2e8f0', background: '#fff',
              fontSize: 14, fontWeight: 600, color: '#64748b',
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 24px', borderRadius: 10,
              border: 'none', background: C.navy,
              fontSize: 14, fontWeight: 600, color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            {saving && <RefreshCw size={16} className="animate-spin" />}
            Enregistrer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Page principale
export default function PlateformeAbonnementsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    gratuit: 0,
    pro: 0,
    business: 0,
    premium: 0,
    mrr: 0,
    arr: 0,
  });

  // Charger les abonnements
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/subscriptions`);
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
        setStats(data.stats || stats);
      } else {
        // Données de démo si l'API n'existe pas encore
        const demoData = generateDemoData();
        setSubscriptions(demoData.subscriptions);
        setStats(demoData.stats);
      }
    } catch (error) {
      console.error('Erreur:', error);
      const demoData = generateDemoData();
      setSubscriptions(demoData.subscriptions);
      setStats(demoData.stats);
    }
    setLoading(false);
  };

  // Générer des données de démo
  const generateDemoData = () => {
    const plans = ['gratuit', 'pro', 'business', 'premium'];
    const statuses = ['active', 'active', 'active', 'pending', 'expired'];
    const companies = [
      'Dupont Plomberie', 'Martin Électricité', 'Durand Menuiserie',
      'Bernard Carrelage', 'Petit Maçonnerie', 'Robert Peinture',
      'Richard Chauffage', 'Moreau Isolation', 'Simon Couverture',
      'Laurent Rénovation', 'Leroy Construction', 'Roux Domotique'
    ];

    const subs = companies.map((company, i) => ({
      _id: `sub_${i}`,
      company_name: company,
      user_name: company.split(' ')[0] + ' Jean',
      email: `contact@${company.toLowerCase().replace(' ', '')}.fr`,
      plan: plans[i % 4],
      status: statuses[i % 5],
      billing_cycle: i % 3 === 0 ? 'annual' : 'monthly',
      created_at: new Date(Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString(),
      last_billing_date: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      next_billing_date: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      notes: '',
    }));

    const gratuit = subs.filter(s => s.plan === 'gratuit').length;
    const pro = subs.filter(s => s.plan === 'pro').length;
    const business = subs.filter(s => s.plan === 'business').length;
    const premium = subs.filter(s => s.plan === 'premium').length;
    
    const mrrPro = pro * 75;
    const mrrBusiness = business * 100;
    const mrrPremium = premium * 125;
    const mrr = mrrPro + mrrBusiness + mrrPremium;

    return {
      subscriptions: subs,
      stats: {
        total: subs.length,
        gratuit, pro, business, premium,
        mrr,
        arr: mrr * 12,
      }
    };
  };

  // Sauvegarder les modifications
  const handleSaveSubscription = async (id, data) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/subscriptions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        fetchSubscriptions();
      } else {
        // Mise à jour locale en cas d'erreur API
        setSubscriptions(prev => prev.map(sub => 
          (sub._id === id || sub.id === id) ? { ...sub, ...data } : sub
        ));
      }
    } catch (error) {
      // Mise à jour locale
      setSubscriptions(prev => prev.map(sub => 
        (sub._id === id || sub.id === id) ? { ...sub, ...data } : sub
      ));
    }
  };

  // Filtrer les abonnements
  const filteredSubscriptions = subscriptions.filter(sub => {
    if (selectedPlan !== 'all' && sub.plan !== selectedPlan) return false;
    if (selectedStatus !== 'all' && sub.status !== selectedStatus) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        sub.company_name?.toLowerCase().includes(query) ||
        sub.user_name?.toLowerCase().includes(query) ||
        sub.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div style={{ padding: '32px 40px', background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, margin: '0 0 8px' }}>
          Gestion des Abonnements
        </h1>
        <p style={{ fontSize: 15, color: '#64748b' }}>
          Gérer les formules, valider les accès et suivre les revenus
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 20, marginBottom: 32 }}>
        <StatCard
          title="Total abonnés"
          value={stats.total}
          icon={Users}
          color={C.navy}
          trend={12}
        />
        <StatCard
          title="Gratuit"
          value={stats.gratuit}
          icon={Star}
          color={C.green}
        />
        <StatCard
          title="Pro"
          value={stats.pro}
          icon={Briefcase}
          color="#3B82F6"
        />
        <StatCard
          title="Business"
          value={stats.business}
          icon={Rocket}
          color={C.orange}
        />
        <StatCard
          title="Premium"
          value={stats.premium}
          icon={Crown}
          color={C.navy}
        />
        <StatCard
          title="MRR"
          value={`${stats.mrr?.toLocaleString()}€`}
          subtitle={`ARR: ${stats.arr?.toLocaleString()}€`}
          icon={CreditCard}
          color={C.green}
          trend={8}
        />
      </div>

      {/* Filtres */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24,
        background: '#fff', padding: 20, borderRadius: 16,
        border: '1px solid #e2e8f0',
      }}>
        {/* Recherche */}
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Rechercher par nom, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px 12px 44px',
              borderRadius: 10, border: '1px solid #e2e8f0',
              fontSize: 14,
            }}
          />
        </div>

        {/* Filtre plan */}
        <select
          value={selectedPlan}
          onChange={(e) => setSelectedPlan(e.target.value)}
          style={{
            padding: '12px 16px', borderRadius: 10,
            border: '1px solid #e2e8f0', fontSize: 14,
            background: '#fff', cursor: 'pointer', minWidth: 150,
          }}
        >
          <option value="all">Toutes les formules</option>
          <option value="gratuit">Gratuit</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
          <option value="premium">Premium</option>
        </select>

        {/* Filtre statut */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{
            padding: '12px 16px', borderRadius: 10,
            border: '1px solid #e2e8f0', fontSize: 14,
            background: '#fff', cursor: 'pointer', minWidth: 150,
          }}
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="pending">En attente</option>
          <option value="expired">Expiré</option>
          <option value="cancelled">Annulé</option>
        </select>

        {/* Export */}
        <button style={{
          padding: '12px 20px', borderRadius: 10,
          border: '1px solid #e2e8f0', background: '#fff',
          fontSize: 14, fontWeight: 500, color: '#64748b',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Download size={16} />
          Exporter
        </button>

        {/* Rafraîchir */}
        <button
          onClick={fetchSubscriptions}
          style={{
            padding: '12px 20px', borderRadius: 10,
            border: 'none', background: C.navy,
            fontSize: 14, fontWeight: 500, color: '#fff',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <RefreshCw size={16} />
          Actualiser
        </button>
      </div>

      {/* Tableau */}
      <div style={{
        background: '#fff', borderRadius: 16,
        border: '1px solid #e2e8f0', overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Client
              </th>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Formule
              </th>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Statut
              </th>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Facturation
              </th>
              <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Inscrit le
              </th>
              <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                  <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
                  Chargement...
                </td>
              </tr>
            ) : filteredSubscriptions.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                  <AlertCircle size={24} style={{ margin: '0 auto 12px' }} />
                  Aucun abonnement trouvé
                </td>
              </tr>
            ) : (
              filteredSubscriptions.map((sub, i) => (
                <tr
                  key={sub._id || sub.id || i}
                  style={{
                    borderTop: '1px solid #e2e8f0',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: 600, color: C.navy, marginBottom: 2 }}>
                      {sub.company_name || sub.user_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{sub.email}</div>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <PlanBadge plan={sub.plan} />
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <StatusBadge status={sub.status} />
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: C.navy }}>
                      {sub.billing_cycle === 'annual' ? 'Annuel' : 'Mensuel'}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {PLANS_CONFIG[sub.plan]?.price[sub.billing_cycle] || 0}€/{sub.billing_cycle === 'annual' ? 'an' : 'mois'}
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px', fontSize: 14, color: '#64748b' }}>
                    {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      <button
                        onClick={() => setSelectedSubscription(sub)}
                        style={{
                          width: 36, height: 36, borderRadius: 8,
                          border: '1px solid #e2e8f0', background: '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Modifier"
                      >
                        <Edit size={16} color="#64748b" />
                      </button>
                      <button
                        style={{
                          width: 36, height: 36, borderRadius: 8,
                          border: '1px solid #e2e8f0', background: '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Envoyer un email"
                      >
                        <Mail size={16} color="#64748b" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedSubscription && (
          <SubscriptionModal
            subscription={selectedSubscription}
            onClose={() => setSelectedSubscription(null)}
            onSave={handleSaveSubscription}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
